import { supabase } from '@/lib/supabase/client'

export interface AdminUser {
  id: string
  email: string
  display_name: string | null
  subscription_tier: string
  subscription_status: string
  billing_cycle: string | null
  trial_started_at: string | null
  stripe_customer_id: string | null
  is_test: boolean
  legacy_caps: boolean
  legacy_business_preview_until: string | null
  layouts_count: number
  locations_count: number
  created_at: string
  last_sign_in_at: string | null
  banned_until: string | null
}

export interface UserListParams {
  page?: number
  pageSize?: number
  search?: string
  tierFilter?: string[]
  statusFilter?: string[]
  includeTestUsers?: boolean
}

export interface UserListResult {
  users: AdminUser[]
  total: number
  page: number
  pageSize: number
}

export interface AdminUserLocation {
  id: string
  name: string
  airport_code: string | null
  airport_name: string | null
  latitude: number | null
  longitude: number | null
  thumbnail_url: string | null
  slug: string | null
  address: string | null
  layout_count: number
  created_at: string
}

export interface AdminUserLayout {
  id: string
  name: string
  location_id: string | null
  thumbnail_url: string | null
  is_public: boolean
  share_token: string | null
  building_count: number
  /** Bays across multi-unit rows (single-volume hangars count 0). */
  unit_count: number
  item_count: number
  /** Airport structures (presets, jet bridges, control towers) — stored as items. */
  structure_count: number
  item_summary: Array<{ name: string; count: number }>
  created_at: string
  updated_at: string
}

export interface AdminUserDetail {
  user: {
    id: string
    email: string
    created_at: string
    last_sign_in_at: string | null
    banned_until: string | null
    app_metadata: Record<string, unknown>
  }
  profile: Record<string, unknown> | null
  locations: AdminUserLocation[]
  layouts: AdminUserLayout[]
  gift_licenses: Array<Record<string, unknown>>
}

async function invokeAdminUsers<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-users', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

export function fetchAdminUsers(params: UserListParams) {
  return invokeAdminUsers<UserListResult>({ action: 'list', ...params })
}

export function fetchAdminUserDetail(userId: string) {
  return invokeAdminUsers<AdminUserDetail>({ action: 'detail', userId })
}

export function updateUserTier(userId: string, tier: string, reason: string) {
  return invokeAdminUsers<{ success: boolean }>({ action: 'update_tier', userId, tier, reason })
}

export function suspendUser(userId: string, reason: string) {
  return invokeAdminUsers<{ success: boolean }>({ action: 'suspend', userId, reason })
}

export function unsuspendUser(userId: string, reason: string) {
  return invokeAdminUsers<{ success: boolean }>({ action: 'unsuspend', userId, reason })
}

export type InternalAccessTier = 'pilot' | 'pro' | 'business'

/** Grants a tier without Stripe. `tier` defaults server-side to 'pro'. */
export function setInternalAccess(userId: string, enable: boolean, tier?: InternalAccessTier) {
  return invokeAdminUsers<{ success: boolean }>({ action: 'set_internal_access', userId, enable, tier })
}

export function setIsTest(userId: string, isTest: boolean) {
  return invokeAdminUsers<{ success: boolean }>({ action: 'set_is_test', userId, is_test: isTest })
}

export interface ImpersonateResult {
  action_link: string
  target_email: string
}

export function impersonateUser(userId: string, reason: string, returnUrl?: string) {
  return invokeAdminUsers<ImpersonateResult>({ action: 'impersonate', userId, reason, returnUrl })
}

export interface GiftLicense {
  id: string
  code: string
  tier: string
  duration_days: number
  buyer_user_id: string
  redeemed_by: string | null
  redeemed_at: string | null
  expires_at: string
  created_at: string
  // Enriched server-side
  status: 'pending' | 'redeemed' | 'expired'
  buyer_email: string | null
  redeemer_email: string | null
}

export interface GiftLicenseListResult {
  licenses: GiftLicense[]
  total: number
  page: number
  pageSize: number
}

export function fetchGiftLicenses(params: { page?: number; pageSize?: number; search?: string }) {
  return invokeAdminUsers<GiftLicenseListResult>({ action: 'list_gift_licenses', ...params })
}
